import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';

import { AxiosError } from 'axios';

import { Breadcrumbs, Error, LoaderSkeleton } from '@utilities';

import {
  readExternalQuestions,
  readInstantSurvey,
  readInstantSurveyType,
} from '@api/instant_survey';
import { readDetails } from '@api/survey_dashboard';
import { readSurvey, readSurveyType, surveyStatus } from '@api/surveys';

import Dashboard from './components/Dashboard';
import SurveyNetworkError from '../../components/SurveyNetworkError';

const Survey = ({ data, publicToken }) => {
  const { atlasInstantSurveyRef, instantSurveyTypeId, surveyId } = data || {};

  const [details, setDetails] = useState();
  const [insightsDocument, setInsightsDocument] = useState();
  const [insightsType, setInsightsType] = useState();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [survey, setSurvey] = useState();
  const [surveyQuestions, setSurveyQuestions] = useState();
  const [surveyType, setSurveyType] = useState();

  const dataRequestController = useRef();

  const navigate = useNavigate();
  const { routes } = useSelector((state) => state?.user?.data);
  const instantSurveyRoutes = routes?.find(({ id }) => id === 'instant-survey-reports')?.routes;

  let { survey_id: paramSurveyId, tab_id: tabId } = useParams();

  const id = parseInt(surveyId || paramSurveyId);

  useEffect(() => {
    if (!tabId) {
      navigate(
        publicToken
          ? `/share/survey/${publicToken}/${id}/details`
          : `/dashboard/survey/${id}/${instantSurveyRoutes?.[0].id}`,
        { replace: true }
      );
    }
    getData();
    return () => dataRequestController?.current?.abort();
  }, [id]);

  const getData = async () => {
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const insightsSurvey = await readSurvey({ id, publicToken, signal });
      const validStatuses = [surveyStatus.COMPLETED, surveyStatus.MET_SAMPLE];
      if (!validStatuses.includes(insightsSurvey.status)) {
        throw new AxiosError('Request failed with status code 404', 'ERR_BAD_REQUEST', null, null, {
          status: 404,
        });
      }

      setInsightsDocument(insightsSurvey);
      if (insightsSurvey.atlasInstantSurveyRef || atlasInstantSurveyRef) {
        const atlasRequests = [
          readDetails({
            publicToken,
            signal,
            surveyId: insightsSurvey.id,
          }),
          readInstantSurvey({
            id: insightsSurvey.atlasInstantSurveyRef || atlasInstantSurveyRef,
            params: { include_response_count: true },
            signal,
          }),
          readInstantSurveyType({
            id: insightsSurvey.instantSurveyTypeId || instantSurveyTypeId,
            signal,
          }),
          readExternalQuestions({
            signal: signal,
            surveySourceId: insightsSurvey.surveySourceId,
          }),
        ];
        const [
          detailsResponse,
          instantSurveyResponse,
          instantSurveyTypeResponse,
          externalQuestionsResponse,
        ] = await Promise.all(atlasRequests);
        const { groups, ...instantSurvey } = instantSurveyResponse;
        setDetails(detailsResponse);
        setSurvey({
          ...instantSurvey,
          // sorting groups here so it never impacts a draft survey
          groups: groups.sort((a, b) => a.quotaGroupRef - b.quotaGroupRef),
        });
        setSurveyQuestions(externalQuestionsResponse);
        setSurveyType(instantSurveyTypeResponse);
      } else {
        const [detailsResponse, surveyTypeResponse, externalQuestionsResponse] = await Promise.all([
          readDetails({
            publicToken,
            signal,
            surveyId: insightsSurvey.id,
          }),
          readSurveyType({
            documentId: insightsSurvey.instantSurveyTypeId || instantSurveyTypeId,
            publicToken,
            signal,
          }),
          readExternalQuestions({
            signal: signal,
            surveySourceId: insightsSurvey.surveySourceId,
          }),
        ]);
        setDetails(detailsResponse);
        setInsightsType(surveyTypeResponse);
        setSurveyQuestions(externalQuestionsResponse);
      }
      setIsDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        setLoadingError(error);
      }
    }
  };

  return (
    <>
      {!publicToken && <Breadcrumbs />}
      {loadingError &&
        (loadingError?.code === 'ERR_NETWORK' ? (
          <SurveyNetworkError />
        ) : (
          <Error status={loadingError?.response?.status} />
        ))}
      {!loadingError && (
        <>
          {isDataLoading ? (
            <LoaderSkeleton height={725}>
              <rect height="95" rx="4" ry="4" width="850" x="0" y="0" />
              <rect height="40" rx="4" ry="4" width="1333" x="0" y="105" />
              <rect height="30" rx="4" ry="4" width="300" x="0" y="155" />
              <rect height="165" rx="4" ry="4" width="1333" x="0" y="195" />
              <rect height="30" rx="4" ry="4" width="300" x="0" y="380" />
              <rect height="58" rx="4" ry="4" width="1333" x="0" y="420" />
              <rect height="58" rx="4" ry="4" width="1333" x="0" y="488" />
              <rect height="58" rx="4" ry="4" width="1333" x="0" y="556" />
            </LoaderSkeleton>
          ) : (
            <Dashboard
              details={details}
              id={id}
              insightsDocument={insightsDocument}
              insightsType={insightsType}
              publicToken={publicToken}
              survey={survey}
              surveyQuestions={surveyQuestions}
              surveyType={surveyType}
            />
          )}
        </>
      )}
    </>
  );
};

Survey.propTypes = {
  data: PropTypes.shape({
    atlasInstantSurveyRef: PropTypes.string,
    instantSurveyTypeId: PropTypes.string,
    surveyId: PropTypes.number,
  }),
  publicToken: PropTypes.string,
};

export default Survey;
